import { BaseService } from "../BaseService";
import { AuthService } from "../auth/auth.service";
import { environment } from '../../../../src/environments/environment';
import { HttpClient } from "@angular/common/http";
import { Router } from '@angular/router';
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { ApiResult } from "../../models/ApiResult";
import { BookingStatusModel } from "../../models/api/BookingStatusModel";
import { BookingStatusFindResultModel } from "../../models/api/BookingStatusFindResultModel";

export abstract class BookingStatusBaseService extends BaseService {

    private urlPart: string = 'BookingStatus';

    constructor(private http: HttpClient, private router: Router, private auth: AuthService) {
        super(http, router, auth);
        this.apiSegmentUrl = this.urlPart;
    };

    /// <summary>
    /// FIND: Retrieves a list of BookingStatus records from the database
    /// /BookingStatus
    /// </summary>
    public findBookingStatus(page: number = 0, size: number = 0, orderBy: string = "", orderDirection: string = "ASC", filter: string = ""): Observable<BookingStatusFindResultModel>
    {
        // Find BookingStatus Records

        const url = `${super.getBaseUrl()}?page=${page}&size=${size}&orderBy=${orderBy}&orderDirection=${orderDirection}&filter=${filter}`;

        if (!environment.production)
            console.log("Find BookingStatus: Calling " + url);

        return this.http.get<BookingStatusFindResultModel>(url)
            .pipe(
                map(next => {
                    let o: BookingStatusFindResultModel = Object.assign(new BookingStatusFindResultModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// GET: Retrieves a single BookingStatus record from the database
    /// /BookingStatus/{bookingstatusid}
    /// </summary>
    /// <param name="bookingstatusid"></param>
    /// <returns></returns>
    public getBookingStatus(bookingstatusid: number): Observable<BookingStatusModel>
    {

        // Get Single BookingStatus Record

        // TODO: Get Single BookingStatus Record

        const url = `${super.getBaseUrl()}/${bookingstatusid}`;

        if (!environment.production)
            console.log("Get BookingStatus: Calling " + url);

        return this.http.get<BookingStatusModel>(url)
            .pipe(
                map(next => {
                    let o: BookingStatusModel = Object.assign(new BookingStatusModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// POST: Adds a new BookingStatus record to the database
    /// /BookingStatus/{bookingstatusid}
    /// </summary>
    /// <param name="bookingstatusid"></param>
    /// <returns></returns>
    public createBookingStatus(newRecord: BookingStatusModel): Observable<BookingStatusModel>
    {
        // Add Single BookingStatus Record

        const url = `${super.getBaseUrl()}`;

        if (!environment.production)
            console.log("Create BookingStatus:Calling " + url);

        return this.http.post<BookingStatusModel>(url, newRecord)
            .pipe(
                map(next => {
                    let o: BookingStatusModel = Object.assign(new BookingStatusModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// PUT: Updates an existing BookingStatus record in the database
    /// /BookingStatus/{bookingstatusid}
    /// </summary>
    /// <param name="bookingstatusid"></param>
    /// <returns></returns>
    public updateBookingStatus(bookingstatusid: number, updatedRecord: BookingStatusModel): Observable<BookingStatusModel>
    {
        // Update Single BookingStatus Record

        const url = `${super.getBaseUrl()}/${bookingstatusid}`;

        if (!environment.production)
            console.log("Update BookingStatus: Calling " + url);

        return this.http.put<BookingStatusModel>(url, updatedRecord)
            .pipe(
                map(next => {
                    let o: BookingStatusModel = Object.assign(new BookingStatusModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public updateBookingStatusRecord(updatedRecord: BookingStatusModel): Observable<BookingStatusModel>
    {
        return this.updateBookingStatus(updatedRecord.bookingStatusID, updatedRecord);
    }

    /// <summary>
    /// DELETE: Deletes an existing BookingStatus record from the database
    /// /BookingStatus/{bookingstatusid}
    /// </summary>
    /// <param name="bookingstatusid"></param>
    /// <returns></returns>
    public deleteBookingStatus(bookingstatusid: number): Observable<ApiResult>
    {
        // Delete Single BookingStatus Record

        const url = `${super.getBaseUrl()}/${bookingstatusid}`;

        if (!environment.production)
            console.log("Delete BookingStatus: Calling " + url);

        return this.http.delete<ApiResult>(url)
            .pipe(
                map(next => {
                    let o: ApiResult = new ApiResult();
                    o.success = true;
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public deleteBookingStatusRecord(deleteRecord: BookingStatusModel): Observable<ApiResult>
    {
        // Delete Single BookingStatus Record
        return this.deleteBookingStatus(deleteRecord.bookingStatusID);
    }

}
